// ref: app/schemas/auth.py
// Mantener sincronizado con AuthMeResponse + sub-schemas del backend.

export type TipoCuenta = 'operativa' | 'super_admin' | 'sistema';

export interface PersonaPerfil {
  id: string;
  nombre_completo: string;
  email: string | null;
  rut: string;
  celular: string | null;
}

export interface UsuarioPerfil {
  id: string;
  tipo_cuenta: TipoCuenta;
  email_login: string;
  activo: boolean;
}

export interface TenenciaPerfil {
  /** Sprint 18 P3 B3: id de la tenencia para invocar
   *  POST /me/tenencias/{id}/cerrar (A1 "Dejé el barrio"). */
  id: string;
  lote_id: string;
  lote_identificador: string;
  lote_sector: string;
  tipo: string;
  responsable_pago_cuotas: boolean;
  // Sprint 18 P2: onboarding del titular sobre esta tenencia.
  wizard_completado?: boolean;
}

/** Casa donde el usuario es AUTORIZADO de portal (residente con
 *  cuenta_portal_activa), NO titular. Va aparte de tenencias_activas para no
 *  gatillar el onboarding ni las acciones de titular; el portal la suma al
 *  selector de casa (lectura del estado de cuenta). */
export interface CasaAutorizadaPerfil {
  lote_id: string;
  lote_identificador: string;
  lote_sector: string;
}

export interface AuthMeResponse {
  persona: PersonaPerfil;
  usuario: UsuarioPerfil;
  roles_vigentes: string[];
  tenencias_activas: TenenciaPerfil[];
  casas_autorizadas?: CasaAutorizadaPerfil[];
}

// ref: app/schemas/lote.py + app/models/cuota.py (ESTADOS_VALIDOS)
export type EstadoCuota = 'pendiente' | 'en_abono' | 'pagada' | 'vencida' | 'condonada';

export interface PagoAplicadoLinea {
  pago_id: string;
  fecha_transferencia: string;
  monto_aplicado: number;
  num_operacion: string;
  /** Descripción específica de la DistribucionPago (S15-03c).
   *  Ej: "Carga inicial — pago marzo 2026" o "Carga inicial — saldo
   *  a favor 2025 aplicado a febrero 2026". El Dashboard usa esto en
   *  lugar de derivar texto del periodo de la cuota destino. */
  descripcion: string;
}

export interface CuotaEstado {
  cuota_id: string;
  periodo: string;
  monto_total: number;
  monto_aplicado: number;
  saldo_pendiente: number;
  estado: EstadoCuota;
  vence_el: string;
  pagos_aplicados: PagoAplicadoLinea[];
}

export interface ProximaCuota {
  cuota_id: string;
  periodo: string;
  monto_total: number;
  saldo_pendiente: number;
  vence_el: string;
}

// ref: app/schemas/lote.py LoteResumen
export interface LoteResumen {
  id: string;
  identificador: string;
  direccion: string;
  sector: string;
  activo: boolean;
  notas: string | null;
  // Nombres de personas con tenencia activa + responsable_pago_cuotas=True.
  // Permite mostrar "Casa 6894 - Pedro Rojas - Poniente" en selectores.
  responsables_pago_nombres: string[];
}

// ref: app/schemas/recurso.py
// Sprint 18 P5: citofonía y acceso_app_wifi salieron del enum de recursos
// (ahora son tablas dedicadas accesos_citofonia / accesos_wifi).
export type TipoRecurso = 'llavero' | 'control_remoto' | 'otro';

export type EstadoRecurso =
  | 'entregado'
  | 'devuelto'
  | 'perdido'
  | 'reemplazado'
  // Sprint 18 P8: ciclo de "reportar problema".
  | 'reportado'
  | 'de_baja';

export interface RecursoConSuspensionRead {
  id: string;
  lote_id: string;
  residente_actual_id: string | null;
  // Sprint 17 S17-19 — para CRs, apunta al vehículo asignado.
  vehiculo_id: string | null;
  tipo: TipoRecurso;
  identificador: string;
  costo_reposicion: number;
  entregado_at: string;
  estado: EstadoRecurso;
  notas: string | null;
  // F11: serial RFID del llavero (null para CR/pila). Para mostrar el serial
  // grabado en el llavero físico junto a la etiqueta LL-NNNN.
  serial_rfid: string | null;
}

// ref: app/schemas/recurso.py RecursoRead. Espejo completo del backend
// post S15-Z (la pagina /tesoreria/recursos consume todos los campos).
export interface RecursoRead {
  id: string;
  lote_id: string;
  residente_actual_id: string | null;
  // Sprint 17 S17-19 — para CRs, apunta al vehículo asignado.
  vehiculo_id: string | null;
  tipo: TipoRecurso;
  identificador: string;
  costo_reposicion: number;
  entregado_at: string;
  disponible_entrega_at: string | null;
  estado: EstadoRecurso;
  reemplaza_a_recurso_id: string | null;
  motivo_reemplazo: string | null;
  ingreso_vario_id: string | null;
  notas: string | null;
  // Serial RFID del llavero (desde stock_recursos vía join). Lo expone el
  // listado (GET /recursos) y el de ente externo; ausente en endpoints que
  // devuelven RecursoRead plano. null si el llavero aún no tiene serial.
  serial_rfid?: string | null;
  creado_at: string;
  actualizado_at: string;
  // 2026-10-06 — baja y desprogramación del hardware.
  baja_at?: string | null;
  motivo_baja?: MotivoBaja | null;
  desprogramado_hw_at?: string | null;
}

// 2026-10-06 — refleja MOTIVOS_BAJA del backend (app/models/recurso.py).
export type MotivoBaja =
  | 'perdido'
  | 'robado'
  | 'llevado_ex_residente'
  | 'danado'
  | 'otro';

/** Llavero/control dado de baja que aún no se borra de motores y puertas.
 *  ref: app/schemas/recurso.py RecursoPorDesprogramarRead */
export interface RecursoPorDesprogramarRead {
  recurso_id: string;
  tipo: TipoRecurso;
  identificador: string;
  serial_rfid: string | null;
  lote_identificador: string | null;
  ente_externo_nombre: string | null;
  ultimo_portador: string | null;
  patente: string | null;
  estado: EstadoRecurso;
  motivo_baja: MotivoBaja | null;
  baja_at: string | null;
}

// === Accesos citofonía / wifi (Sprint 18 P5) ===
// ref: app/schemas/accesos.py
export type EstadoAcceso = 'pendiente_validacion' | 'activo' | 'baja';

export interface AccesoCitofoniaRead {
  id: string;
  lote_id: string;
  residente_id: string;
  slot: number; // 1-4
  estado: EstadoAcceso;
  celular_snapshot: string | null;
  validado_por_usuario_id: string | null;
  validado_at: string | null;
  motivo_rechazo: string | null;
  motivo_pendiente: string | null;
  motivo_reprogramacion: string | null;
  creado_at: string;
  actualizado_at: string;
}

export interface AccesoWifiRead {
  id: string;
  lote_id: string;
  residente_id: string;
  estado: EstadoAcceso;
  email_snapshot: string | null;
  validado_por_usuario_id: string | null;
  validado_at: string | null;
  motivo_rechazo: string | null;
  motivo_pendiente: string | null;
  motivo_reprogramacion: string | null;
  creado_at: string;
  actualizado_at: string;
  /** Cruce de los tres ejes (autorizado / ejecutado / suspendido). Lo
   *  calcula el backend; `estado` a secas no alcanza para pintar. */
  estado_efectivo?: string | null;
}

export interface SolicitarCitofoniaPayload {
  residente_id: string;
  slot: number; // 1-4
  motivo?: string | null;
}

export interface SolicitarWifiPayload {
  residente_id: string;
  motivo?: string | null;
}

/** Máximo de accesos wifi activos por casa. Espejo de MAX_WIFI_POR_LOTE
 * del backend (app/models/acceso_wifi.py). */
export const MAX_WIFI_POR_LOTE = 8;

// Suspensión por morosidad de accesos (Sprint 18 P5 B3).
// ref: app/schemas/suspension_acceso.py
export type AccesoTipo = 'citofonia' | 'wifi';

export interface SuspensionAccesoRead {
  id: string;
  acceso_tipo: AccesoTipo;
  acceso_id: string;
  suspendido_at: string;
  suspendido_por_usuario_id: string;
  motivo_suspension: string;
  levantada_at: string | null;
  levantada_por_usuario_id: string | null;
  motivo_levantamiento: string | null;
  creado_at: string;
  actualizado_at: string;
}

export interface SuspensionAccesoAplicarPayload {
  acceso_tipo: AccesoTipo;
  acceso_id: string;
  motivo_suspension: string;
  suspendido_por_usuario_id: string;
}

export interface SuspensionAccesoLevantarPayload {
  motivo_levantamiento: string;
  levantada_por_usuario_id: string;
}

// Suspensión POR CASA (Sprint 19a — Suspensiones-rediseño, Opción B).
// ref: app/schemas/suspension_lote.py
export type EstadoSuspensionLote =
  | 'en_proceso_suspension'
  | 'suspendido'
  | 'en_proceso_reactivacion'
  | 'activo';

// Acceso que el operador debe tocar en el HW: alta nueva ('programar') o
// cambio de dato ('reprogramar').
export interface ReprogramacionPendienteRead {
  tipo: AccesoTipo;
  tipo_trabajo:
    | 'programar'
    | 'reprogramar'
    | 'eliminar'
    | 'desprogramar_suspension' // casa que se suspende: quitar de la central
    | 'programar_reactivacion'; // casa que se reactiva: volver a programar
  acceso_id: string;
  lote_id: string;
  lote_identificador: string;
  residente_id: string;
  residente_nombre: string | null;
  parentesco: string | null;
  slot: number | null;
  valor_actual: string | null; // lo que el HW tiene hoy (null si es alta)
  valor_nuevo: string | null; // lo que debe quedar
}

export interface SuspensionLoteRead {
  id: string;
  lote_id: string;
  estado: EstadoSuspensionLote;
  motivo_suspension: string;
  suspension_iniciada_por_usuario_id: string;
  suspension_iniciada_at: string;
  suspension_confirmada_por_usuario_id: string | null;
  suspension_confirmada_at: string | null;
  motivo_reactivacion: string | null;
  reactivacion_iniciada_por_usuario_id: string | null;
  reactivacion_iniciada_at: string | null;
  reactivacion_confirmada_por_usuario_id: string | null;
  reactivacion_confirmada_at: string | null;
  creado_at: string;
  actualizado_at: string;
}

// Tab 1: casa candidata a suspensión (nivel calculado EN VIVO).
export interface CandidatoSuspensionRead {
  lote_id: string;
  identificador: string;
  responsable: string | null; // titular responsable de pago; null si no hay
  nivel: string; // suspendible (convención #20)
  monto_adeudado: number;
}

// Tab 2: suspensión vigente/en proceso + orden de trabajo de HW.
export interface SuspensionVigenteRead {
  suspension: SuspensionLoteRead;
  identificador: string;
  // Celulares de la casa que esperan en Reprogramación (2026-10-07).
  citofonias_pendientes: number;
  al_dia: boolean; // aviso NO bloqueante al reactivar
  monto_adeudado: number;
}

export interface SuspenderCasaPayload {
  motivo: string;
}

// === Stock de recursos (Sprint 17 S17-04 / UI S17-13) ===

export type TipoStock = 'llavero' | 'control_remoto' | 'pila' | 'otro';
export type EstadoStock =
  | 'en_stock'
  | 'asignado'
  | 'consumido'
  | 'dado_de_baja';

export interface StockRecursoItemLlavero {
  identificador: string;
  serial_rfid: string;
}

export interface StockRecursoIngresar {
  tipo: TipoStock;
  costo_compra: number;
  valor_venta: number;
  cantidad: number;
  items?: StockRecursoItemLlavero[] | null;
  notas?: string | null;
}

export interface StockRecursoRead {
  id: string;
  tipo: TipoStock;
  identificador: string | null;
  serial_rfid: string | null;
  costo_compra: number;
  valor_venta: number;
  ingresado_at: string;
  estado: EstadoStock;
  recurso_id: string | null;
  // Identificador legible del recurso vinculado (ej. 'CR-6834-1') para UI,
  // en vez del UUID. null en venta suelta; ausente en respuestas que no
  // eager-cargan el recurso. RP-04.
  recurso_identificador?: string | null;
  // Casa a la que se asignó el recurso (ej. '6935'), destino legible en vez del
  // UUID. null si es de ente externo / venta suelta.
  recurso_lote_identificador?: string | null;
  consumido_at: string | null;
  notas: string | null;
  creado_at: string;
  actualizado_at: string;
}

export interface StockRecursoDisponibleResumen {
  tipo: TipoStock;
  cantidad_disponible: number;
  valor_venta_proximo: number | null;
}

// === Pilas (Sprint 19a RP-04 — venta en un solo paso) ===

// ref: app/schemas/stock_recurso.py PilaVentaIn
// Venta de pila(s) en UN SOLO PASO: descuenta stock (FIFO) + genera el cobro.
// El control (cr_referencia_id) es referencia opcional (null = venta suelta de
// repuesto). El monto lo calcula el backend desde valor_venta del stock.
export interface PilaVentaIn {
  lote_id: string;
  cantidad: number;
  cr_referencia_id?: string | null;
  residente_id?: string | null;
  descripcion_extra?: string | null;
}

// === Asignación directa (Sprint 17 S17-04b / UI S17-13b) ===
// Vía de excepción D-S17-11 para que el tesorero registre una asignación
// fuera del flujo de solicitud del vecino.

export type TipoAsignacionDirecta = 'llavero' | 'control_remoto';

export interface RecursoAsignacionDirectaIn {
  tipo: TipoAsignacionDirecta;
  lote_id: string;
  residente_id: string;
  vehiculo_id?: string | null;
  monto_a_cobrar: number;
  justificacion: string;
}

export type EstadoSolicitudRecurso =
  | 'pendiente_aprobacion'
  | 'aprobada'
  | 'esperando_cobro'
  | 'esperando_retiro'
  | 'esperando_asignacion'
  | 'asignada'
  | 'rechazada'
  | 'cancelada_por_cascada'
  | 'cancelada_manual';

export interface SolicitudRecursoRead {
  id: string;
  residente_id: string;
  lote_id: string;
  vehiculo_id: string | null;
  tipo: 'llavero' | 'control_remoto' | 'otro';
  motivo: string;
  motivo_texto: string | null;
  estado: EstadoSolicitudRecurso;
  ingreso_vario_id: string | null;
  recurso_id: string | null;
  // Sprint 18 P8: recurso original reportado (NULL en solicitudes nuevas).
  recurso_origen_id: string | null;
  solicitado_at: string;
  revisado_at: string | null;
  revisado_por_usuario_id: string | null;
  aprobada_at: string | null;
  cancelada_at: string | null;
  motivo_aprobacion: string | null;
  motivo_rechazo: string | null;
  notas: string | null;
  creado_at: string;
  actualizado_at: string;
}

/**
 * Sprint 17 S17-12 tab Solicitudes — variante enriquecida
 * (GET /solicitudes-recurso/con-detalle).
 */
export interface SolicitudRecursoConDetalle extends SolicitudRecursoRead {
  lote_identificador: string;
  vecino_nombre: string;
  monto_a_cobrar: number | null;
  iv_estado: string | null;
}

/**
 * Sprint 17 S17-16 — POST /me/solicitudes-recurso (vecino crea
 * solicitud). Doctrina D-S17-9: enum cerrado de motivos.
 */
export type TipoSolicitudRecurso = 'llavero' | 'control_remoto' | 'otro';

export type MotivoSolicitud =
  | 'perdida'
  | 'robo'
  | 'deterioro'
  | 'falla_fabrica'
  | 'nuevo_residente'
  | 'nuevo_vehiculo'
  | 'otro';

export interface SolicitudRecursoCreate {
  residente_id: string;
  lote_id: string;
  vehiculo_id?: string | null;
  tipo: TipoSolicitudRecurso;
  motivo: MotivoSolicitud;
  motivo_texto?: string | null;
  notas?: string | null;
}

// ref: app/schemas/solicitud_acceso.py
export type VinculoDeclarado =
  | 'propietario'
  | 'arrendatario'
  | 'familiar_propietario'
  | 'otro';

export type EstadoSolicitudAcceso = 'pendiente' | 'aprobada' | 'rechazada';

// Sprint 12 S12-17 + Sprint 17 Z21: el único rol asignable al aprobar
// es 'vecino'. La distinción propietario/arrendatario/etc vive en
// Tenencia.tipo, no en persona_roles.
export type RolAsignado = 'vecino';

export interface SolicitudAccesoRead {
  id: string;
  nombre_completo: string;
  rut: string;
  lote_solicitado: string;
  email: string;
  telefono: string | null;
  vinculo_declarado: VinculoDeclarado;
  // Sprint 17 Z21: fecha desde cuando el solicitante habita/posee
  // (puede ser null para solicitudes pre-Z21).
  tenencia_desde: string | null;
  estado: EstadoSolicitudAcceso;
  revisado_por_usuario_id: string | null;
  revisado_at: string | null;
  comentario: string | null;
  ip_solicitante: string | null;
  solicitado_at: string;
  // Fase 4: nombre de la Persona pre-cargada que matchea por RUT canónico
  // (null si el RUT no corresponde a nadie cargado). Permite a la comisión
  // detectar variantes del nombre o suplantaciones.
  persona_existente_nombre: string | null;
  /** Email del padrón para ese RUT. Al aprobar, el correo se vuelve el
   *  login de la cuenta: la Comisión compara antes de confirmar. */
  persona_existente_email: string | null;
  /** Solicitudes ya resueltas de este mismo RUT. Si vienen aprobadas
   *  previas, lo más probable es que su cuenta nunca se provisionó. */
  previas_aprobadas: number;
  previas_rechazadas: number;
  /** Casas donde ese RUT YA tiene tenencia activa. `lote_solicitado` es
   *  texto libre: un dígito mal tipeado apunta a un lote que existe y la
   *  aprobación entra a la casa de otro vecino. Vacío = no está en el padrón. */
  persona_existente_lotes: string[];
}

export interface SolicitudAccesoAprobarPayload {
  rol_asignado: RolAsignado;
  // Sprint 17 Z21: la comisión confirma el lote real y los detalles
  // de la Tenencia al aprobar.
  lote_id: string;
  tipo_tenencia: TipoTenencia;
  tenencia_desde: string;
  responsable_pago_cuotas: boolean;
  // Fase 4: nombre confirmado/normalizado por la comisión. Sobrescribe
  // Persona.nombre_completo al aprobar. Si se omite, conserva el existente.
  nombre_completo?: string | null;
  /** Correo confirmado por la Comisión: se vuelve el `email_login` de la
   *  cuenta. Si se omite, se usa el que escribió el solicitante. */
  email?: string | null;
  comentario?: string | null;
  /** Confirmación explícita para aprobar en una casa distinta de aquella
   *  donde el RUT ya figura. Sin esto el backend responde 409. */
  confirmar_casa_distinta?: boolean;
}

/** Texto que recibe el solicitante al ser rechazado. El `comentario` de la
 *  Comisión nunca se envía (puede tener info sensible y un correo se
 *  reenvía); esta plantilla es la forma de dar contexto sin exponerlo. */
export type PlantillaRechazoAcceso =
  | 'estandar'
  | 'solo_titular'
  | 'ya_tiene_cuenta';

export interface SolicitudAccesoRechazarPayload {
  comentario: string;
  plantilla_email?: PlantillaRechazoAcceso;
}

export interface SolicitudAccesoCreatePayload {
  nombre_completo: string;
  rut: string;
  lote_solicitado: string;
  email: string;
  telefono?: string | null;
  vinculo_declarado: VinculoDeclarado;
  // Sprint 17 Z21: fecha (ISO YYYY-MM-DD) desde la cual el solicitante
  // habita/posee el inmueble.
  tenencia_desde: string;
  comentario?: string | null;
  turnstile_token: string;
}

/** Aviso de morosidad que ve el vecino en su resumen (convención #20). */
export interface AvisoMorosidadPortal {
  tipo: 'deudor' | 'moroso' | 'suspendible' | 'suspendida';
  titulo: string;
  mensaje: string;
}

export interface EstadoCuentaResponse {
  lote_id: string;
  identificador: string;
  cuotas: CuotaEstado[];
  saldo_total_pendiente: number;
  // Sprint 17 S17-Z5: saldo del semestre actual completo + arrastre.
  // Mostrar en la vista vecino "Cuota semestral: te faltan aún $X".
  saldo_semestre_actual: number;
  // Aviso del resumen (convención #20): lo arma el backend con el mismo texto
  // que la campana. null si la casa está al día.
  aviso_morosidad: AvisoMorosidadPortal | null;
  deuda_vencida_arrastre: number;
  meses_cubiertos_anio_actual: number;
  mtd_acumulado_a_hoy: number;
  atraso_mensual: number;
  saldo_a_favor_actual: number;
  proxima_cuota_a_vencer: ProximaCuota | null;
  monto_mensual_equivalente: number;
  monto_semestral: number;
  ultima_actividad: string | null;
}

// Sprint 12 S12-11. Vehiculo asociado al lote.
// Sprint 13 S13-33: workflow de validacion (mismo patron que residentes).
export type EstadoVehiculo =
  | 'pendiente_validacion'
  | 'aprobado'
  | 'rechazado'
  | 'inactivo';

// Sprint 14 S14-08: color como lista cerrada. Mantener en sync con
// `app.schemas.vehiculo.ColorVehiculoLiteral` y `COLORES_VALIDOS` en
// el modelo backend.
export type ColorVehiculo =
  | 'Blanco'
  | 'Negro'
  | 'Gris'
  | 'Plata'
  | 'Rojo'
  | 'Azul'
  | 'Café'
  | 'Otro';

export const COLORES_VEHICULO: readonly ColorVehiculo[] = [
  'Blanco', 'Negro', 'Gris', 'Plata', 'Rojo', 'Azul', 'Café', 'Otro',
] as const;

/** Sprint 18 P6: tipo de tracción. `combustion` = sin baterías de
 *  litio (bencina/diesel). `hibrido`/`electrico` = patente verde. */
export type TipoTraccion = 'combustion' | 'hibrido' | 'electrico';

export interface VehiculoRead {
  id: string;
  lote_id: string;
  patente: string;
  marca: string | null;
  modelo: string | null;
  color: ColorVehiculo | null;
  color_otro: string | null;
  activo: boolean;
  estado: EstadoVehiculo;
  validado_por_usuario_id: string | null;
  validado_at: string | null;
  motivo_rechazo: string | null;
  notas: string | null;
  /** S15-Vec.2: justificacion del vecino al editar un vehiculo
   *  aprobado. Solo poblado mientras esta en pendiente_validacion. */
  motivo_pendiente?: string | null;
  /** Sprint 18 P6: tipo de tracción para identificar vehículos con
   *  baterías de litio (patente verde) por seguridad/bomberos. */
  tipo_traccion: TipoTraccion;
  creado_at: string;
  actualizado_at: string;
}

// Sprint 12 S12-07. Documento interno publicado por la comision.
export type TipoDocumento = 'reglamento' | 'acta' | 'informe' | 'otro';

export interface DocumentoRead {
  id: string;
  titulo: string;
  descripcion: string | null;
  url_externa: string;
  tipo: TipoDocumento;
  publicado_at: string;
  vigente_desde: string;
  vigente_hasta: string | null;
  creado_por_usuario_id: string;
  creado_at: string;
  actualizado_at: string;
}

// Sprint 12 S12-08. Aviso publicado por la comision al barrio.
export type TipoAviso = 'informativo' | 'urgente' | 'evento';

export interface AvisoRead {
  id: string;
  titulo: string;
  cuerpo: string;
  tipo: TipoAviso;
  publicado_at: string;
  vigente_hasta: string | null;
  creado_por_usuario_id: string;
  creado_at: string;
  actualizado_at: string;
}

// === Pagos y clasificacion multi-concepto (Sprint 13 S13-04, migrado
// desde frontend-superadmin). Tipos alineados con app/schemas/pago.py y
// app/schemas/distribucion_pago.py del backend.

export type EstadoPago =
  | 'por_distribuir'
  | 'distribuido'
  | 'aprobado'
  | 'rechazado';

// Codigos validos del filtro `banco` en GET /pagos/?banco=. Backend
// los mapea a TODOS los TEMPLATE_ID de ese banco via BANCOS_CONOCIDOS
// (Sprint 12 S12-02; multi-template desde 2026-08). Mantener sincronizado
// con `BANCOS_CONOCIDOS` de `apps/backend/app/api/pagos.py`.
export const BANCOS_CODIGOS = [
  'banco_chile',
  'banco_consorcio',
  'banco_estado',
  'banco_falabella',
  'bci',
  'itau',
  'mach',
  'santander',
  'scotiabank',
] as const;
export type BancoCodigo = (typeof BANCOS_CODIGOS)[number];

// Etiquetas legibles de los bancos para combos de UI.
export const BANCOS_LABEL: Record<BancoCodigo, string> = {
  banco_chile: 'Banco de Chile',
  banco_consorcio: 'Banco Consorcio',
  banco_estado: 'BancoEstado',
  banco_falabella: 'Banco Falabella',
  bci: 'BCI',
  itau: 'Itaú',
  mach: 'MACH',
  santander: 'Santander',
  scotiabank: 'Scotiabank',
};

// Body de POST /pagos/manual (Sprint 21 S21-B1). El tesorero registra a mano
// un pago cuyo aviso del banco no llegó por correo. El backend autollena los
// campos internos del modelo PAGO (mensaje_email, email_template_detectado).
export interface PagoManualInput {
  monto_total: number;
  fecha_transferencia: string; // ISO yyyy-mm-dd
  num_operacion: string;
  remitente_nombre: string;
  banco?: BancoCodigo | null;
  motivo?: string | null;
}

export interface PagoRead {
  id: string;
  lote_id: string | null;
  persona_id: string | null;
  monto_total: number;
  fecha_transferencia: string;
  num_operacion: string;
  remitente_nombre: string;
  remitente_email: string | null;
  mensaje_email: string;
  email_template_detectado: string;
  comprobante_url: string | null;
  estado: EstadoPago;
  compensacion_id: string | null;
  aprobado_por_usuario_id: string | null;
  aprobado_at: string | null;
  creado_at: string;
  actualizado_at: string;
  // Derivado del backend (Lote.identificador). Permite mostrar
  // "Casa 6934" en UI sin truncar el UUID.
  lote_identificador: string | null;
  // Derivado: true si lo aprobó la auto-distribución (usuario Sistema), no
  // un tesorero. Para el badge "Automático" + aviso de recientes.
  aprobado_automaticamente?: boolean;
}

export interface ListPagosFiltros {
  estado?: EstadoPago;
  lote_id?: string;
  banco?: BancoCodigo;
  search?: string;
}

// Cuotas — version completa del backend, usada por el modal de
// clasificacion (distinto de CuotaEstado que es subset del Dashboard).

export interface CuotaRead {
  id: string;
  lote_id: string;
  tenencia_responsable_id: string;
  periodo: string;
  monto_total: number;
  vence_el: string;
  estado: EstadoCuota;
  es_arrastre: boolean;
  motivo_condonacion: string | null;
  condonado_por_usuario_id: string | null;
  condonado_at: string | null;
  creado_at: string;
  actualizado_at: string;
}

// Categorias del barrio (gastos/ingresos).

export type TipoCategoria = 'ingreso' | 'gasto';

export interface CategoriaRead {
  id: string;
  nombre: string;
  tipo: TipoCategoria;
  color: string;
  activa: boolean;
  // Sprint 13 S13-30: jerarquía de 2 niveles. NULL = raíz; con valor =
  // hoja con su categoría padre. Una raíz puede ser terminal (sin hijas,
  // seleccionable) o nodo (con hijas, NO seleccionable como categoría).
  categoria_padre_id: string | null;
}

// Clasificar pago multi-concepto — Sprint 11 v2 S11-V2-10, consumido
// por la UI tesoreria del Sprint 13 S13-04.

export type TipoDistribucion =
  | 'cuota'
  | 'multa'
  | 'otro_ingreso'
  | 'venta_recurso'
  | 'aporte_proyecto'
  | 'saldo_a_favor';

export interface LineaDistribucionInput {
  tipo: TipoDistribucion;
  descripcion: string;
  monto_aplicado: number;
  cuota_id?: string | null;
  recurso_id?: string | null;
  proyecto_id?: string | null;
  categoria_id?: string | null;
  // venta_recurso: ancla al IngresoVario `por_cobrar` a cobrar (solicitud del
  // vecino / asignación directa). El recurso se asigna desde stock post-cobro.
  ingreso_vario_id?: string | null;
}

export interface ClasificarPagoInput {
  lote_id: string;
  lineas: LineaDistribucionInput[];
  aprender_alias?: boolean;
}

export interface ClasificarPagoResponse {
  pago: PagoRead;
  ingresos_varios_creados: number;
  alias_aprendido: boolean;
  email_enviado: boolean;
}

export interface SugerenciaLinea {
  tipo: string; // 'cuota' o 'saldo_a_favor'.
  cuota_id: string | null; // null para la linea saldo_a_favor.
  monto: number;
}

export interface SugerenciaDistribucionPropuesta {
  lote_id: string;
  persona_id: string;
  monto: number;
  ruta: string;
  lineas: SugerenciaLinea[];
}

export interface SugerenciaDistribucionRead {
  sugerencia: SugerenciaDistribucionPropuesta | null;
  motivo: string | null;
}

// === Tenencias (Sprint 13 S13-05 + S13-07, migrado desde
// superadmin para alimentar el selector del modal de creacion de
// compensaciones y el listado de /admin/tenencias).

export const TIPOS_TENENCIA = [
  'propietario_residente',
  'propietario_no_residente',
  'arrendatario',
  'comodatario',
  'otro',
] as const;
export type TipoTenencia = (typeof TIPOS_TENENCIA)[number];

// Etiqueta usuario-facing por tipo (decisiones cerradas en S12-17).
export const ETIQUETA_TIPO_TENENCIA: Record<string, string> = {
  propietario_residente: 'Propietario residente',
  propietario_no_residente: 'Propietario no residente',
  arrendatario: 'Arrendatario',
  comodatario: 'Comodato',
  otro: 'Otro vínculo',
};

export interface TenenciaConPersonaLote {
  id: string;
  lote_id: string;
  lote_identificador: string;
  lote_sector: string;
  persona_id: string;
  persona_nombre_completo: string;
  persona_rut: string;
  persona_activa: boolean;
  tipo: string;
  desde: string;
  hasta: string | null;
  es_actual: boolean;
  responsable_pago_cuotas: boolean;
  motivo_cierre: string | null;
  creado_at: string;
  actualizado_at: string;
}

export interface ListTenenciasFiltros {
  lote_id?: string;
  persona_id?: string;
  activas?: boolean;
  sector?: string;
}

// Cerrar tenencia (flujo ex-vecino) — Sprint 13 S13-07 (migrado desde
// frontend-superadmin junto con la UI).
// Sprint 18 P3: notas_cierre opcional + counters de side-effects
// expuestos en el response.
export interface CerrarTenenciaInput {
  fecha_cierre: string;
  motivo_cierre?: string | null;
  notas_cierre?: string | null;
  /** True si la tenencia nunca debió existir. Salta la cascada sobre el
   *  hogar y mueve a los residentes a la tenencia que queda. Solo staff:
   *  el endpoint del vecino no la propaga. */
  es_correccion_registro?: boolean;
}

export interface CerrarTenenciaResponse {
  tenencia: TenenciaConPersonaLote;
  persona_desactivada: boolean;
  residentes_inactivados: number;
  vehiculos_inactivados: number;
  recursos_a_bolsa: number;
  solicitudes_canceladas: number;
  notificaciones_comision_creadas: number;
  /** Residentes que pasaron a la tenencia que queda (corrección). */
  residentes_movidos: number;
}

// === Compensaciones (pago en especie) — Sprint 13 S13-05, migrado
// desde frontend-superadmin tras la doctrina Portal vs Superadmin.

export interface CompensacionRead {
  id: string;
  lote_id: string;
  tenencia_id: string;
  descripcion: string;
  categoria_id: string;
  monto: number;
  periodicidad: 'unica' | 'mensual' | 'bimestral' | 'semestral' | 'anual';
  proyecto_id: string | null;
  vigente_desde: string;
  vigente_hasta: string | null;
  aprobado_acta_url: string | null;
  notas: string | null;
  activa: boolean;
  motivo_cierre: string | null;
  cerrado_at: string | null;
  cerrado_por_usuario_id: string | null;
  // Sprint 17 Z8 — una sola aplicación total por compensación.
  aplicada_at: string | null;
  aplicada_por_usuario_id: string | null;
  creado_at: string;
  actualizado_at: string;
}

export interface CompensacionUpdate {
  descripcion?: string;
  monto?: number;
  periodicidad?: 'unica' | 'mensual' | 'bimestral' | 'semestral' | 'anual';
  vigente_desde?: string;
  vigente_hasta?: string | null;
  aprobado_acta_url?: string | null;
  notas?: string | null;
  // `activa` no es editable por PUT: desactivar va por POST /cerrar.
}

export interface CerrarCompensacionInput {
  motivo_cierre: string;
  vigente_hasta?: string | null;
}

/**
 * Sprint 17 Fase 2 — variante enriquecida que devuelve
 * GET /compensaciones/con-detalle. Agrega campos derivados que la UI
 * de cards necesita para renderizar stats + grid sin N llamadas extra.
 */
export interface AplicacionCompensacion {
  gasto_id: string;
  pago_id: string | null;
  periodo: string | null;
  monto: number;
  fecha: string;
  anulado: boolean;
}

/**
 * Lo que la compensación pagó de UNA cuota (2026-10-06). `meses` en formato
 * `YYYY-MM`, con la misma regla que la grilla de Casas (backend
 * `services/cobertura_meses.py`). `abono_parcial` = aporte que no completa
 * un mes.
 */
export interface CoberturaCompensacion {
  periodo: string;
  monto_aplicado: number;
  meses: string[];
  abono_parcial: number;
}

export interface CompensacionConDetalle extends CompensacionRead {
  lote_identificador: string;
  vecino_nombre: string;
  categoria_nombre: string;
  consumido: number;
  n_aplicaciones: number;
  ultima_aplicacion_fecha: string | null;
  aplicaciones: AplicacionCompensacion[];
  cobertura: CoberturaCompensacion[];
}

export interface CompensacionCreate {
  lote_id: string;
  tenencia_id: string;
  descripcion: string;
  categoria_id: string;
  monto: number;
  periodicidad: 'unica' | 'mensual' | 'bimestral' | 'semestral' | 'anual';
  proyecto_id?: string | null;
  vigente_desde: string;
  vigente_hasta?: string | null;
  aprobado_acta_url?: string | null;
  notas?: string | null;
  activa?: boolean;
}

/**
 * Sprint 17 Z8 — "Aplicar compensación" rework. Body simplificado:
 * solo fecha + descripcion/proveedor opcionales. El backend calcula
 * el desglose greedy automáticamente.
 */
export interface AplicarCompensacionInput {
  fecha_aplicacion: string;
  descripcion_gasto?: string | null;
  proveedor?: string | null;
}

export interface AplicarCompensacionResponse {
  compensacion: CompensacionRead;
  gasto: {
    id: string;
    monto: number;
    fecha: string;
    proveedor: string;
    descripcion: string;
  };
  pago_virtual: {
    id: string;
    monto_total: number;
    num_operacion: string;
    estado: string;
    compensacion_id: string;
  };
  distribuciones_creadas: number;
  cuotas_actualizadas: string[];
  // Sprint 17 Z8 — monto que sobró tras cubrir cuotas, queda como
  // saldo_a_favor del lote.
  sobrante_a_saldo_a_favor: number;
}

/**
 * Sprint 17 Z8 — GET /compensaciones/{id}/preview-aplicacion.
 * Devuelve el desglose greedy sin escribir nada (para alimentar el
 * modal de Aplicar que muestra preview read-only antes de confirmar).
 */
export interface LineaPreviewAplicacion {
  cuota_id: string;
  cuota_periodo: string;
  cuota_vence_el: string;
  saldo_antes: number;
  monto_aplicado: number;
  saldo_despues: number;
}

export interface PreviewAplicacionResponse {
  compensacion_id: string;
  monto_total: number;
  lineas: LineaPreviewAplicacion[];
  sobrante_a_saldo_a_favor: number;
  descripcion_gasto_propuesta: string;
}

// === Tipos admin de documentos y avisos (Sprint 13 S13-06, migrado
// desde frontend-superadmin para soportar la publicacion bajo Vista
// Comision del portal).

export const TIPOS_DOCUMENTO = ['reglamento', 'acta', 'informe', 'otro'] as const;

export const ETIQUETA_TIPO_DOCUMENTO: Record<TipoDocumento, string> = {
  reglamento: 'Reglamento',
  acta: 'Acta',
  informe: 'Informe',
  otro: 'Otro',
};

export interface DocumentoCreate {
  titulo: string;
  descripcion?: string | null;
  url_externa: string;
  tipo: TipoDocumento;
  vigente_desde: string;
  vigente_hasta?: string | null;
}

export const TIPOS_AVISO = ['informativo', 'urgente', 'evento'] as const;

export const ETIQUETA_TIPO_AVISO: Record<TipoAviso, string> = {
  informativo: 'Informativo',
  urgente: 'Urgente',
  evento: 'Evento',
};

export interface AvisoCreate {
  titulo: string;
  cuerpo: string;
  tipo: TipoAviso;
  vigente_hasta?: string | null;
}

export interface AvisoCreadoResponse {
  aviso: AvisoRead;
  fan_out_emails_enviados: number;
  fan_out_emails_fallidos: number;
  fan_out_notificaciones_creadas: number;
}
